import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DOMPurify from 'dompurify';

const COLLAPSED_HEIGHT = 60;

function isEmptyHtml(html) {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
}

function sanitize(html) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 'a', 'ul', 'ol', 'li', 'span', 'font'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class', 'data-list', 'color'],
  });
}

export default function RichTextDisplay({
  value,
  collapsedHeight = COLLAPSED_HEIGHT,
  className = '',
  'data-testid': testId,
}) {
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      setNeedsExpand(contentRef.current.scrollHeight > collapsedHeight + 8);
    }
  }, [value, collapsedHeight]);

  if (isEmptyHtml(value)) {
    return (
      <p className="text-xs text-muted-foreground italic" data-testid={testId}>
        No content
      </p>
    );
  }

  return (
    <div className={`rtd-wrapper ${className}`} data-testid={testId || 'rich-text-display'}>
      <div
        ref={contentRef}
        className="rtd-content ql-editor"
        style={{
          maxHeight: expanded ? 'none' : `${collapsedHeight}px`,
          overflow: expanded ? 'visible' : 'hidden',
        }}
        dangerouslySetInnerHTML={{ __html: sanitize(value) }}
      />
      {needsExpand && (
        <button
          type="button"
          className="rtd-expand-toggle"
          onClick={(e) => { e.stopPropagation(); setExpanded(x => !x); }}
          data-testid={testId ? `${testId}-expand` : 'rtd-expand-toggle'}
        >
          {expanded ? (
            <><ChevronUp className="h-3 w-3" /> Less</>
          ) : (
            <><ChevronDown className="h-3 w-3" /> More</>
          )}
        </button>
      )}
      <style>{`
        .rtd-wrapper {
          position: relative;
        }
        .rtd-content {
          font-size: 13px;
          line-height: 1.5;
          color: hsl(var(--foreground));
          padding: 0;
          transition: max-height 0.2s ease;
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .rtd-content p {
          margin: 0 0 4px 0;
        }
        .rtd-content ul, .rtd-content ol {
          padding-left: 20px;
          margin: 4px 0;
        }
        .rtd-content a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
        /* Checklist display styling */
        .rtd-content li[data-list="checked"],
        .rtd-content li[data-list="unchecked"] {
          list-style: none;
          position: relative;
          padding-left: 4px;
        }
        .rtd-content li[data-list="checked"]::before {
          content: '\\2611';
          margin-right: 6px;
          color: hsl(var(--primary));
        }
        .rtd-content li[data-list="unchecked"]::before {
          content: '\\2610';
          margin-right: 6px;
          color: hsl(var(--muted-foreground));
        }
        .rtd-content li[data-list="checked"] {
          text-decoration: line-through;
          color: hsl(var(--muted-foreground));
        }
        .rtd-expand-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          width: 100%;
          padding: 2px 0;
          border: none;
          background: transparent;
          color: hsl(var(--muted-foreground));
          font-size: 11px;
          cursor: pointer;
          transition: color 0.15s;
        }
        .rtd-expand-toggle:hover {
          color: hsl(var(--primary));
        }
      `}</style>
    </div>
  );
}
